/**
 * Parses and formats policy numbers using the template syntax:
 *   LRSBR<$baseNumber,6>-<$renewalNumber,2>
 * where <$varName,N> means: zero-pad the variable to N digits.
 * Anything outside angle brackets is literal text.
 */

const VARIABLE_PATTERN = /<\$(\w+),(\d+)>/g;

/**
 * Format a policy number using the given template.
 * @param template - e.g. "LRSBR<$baseNumber,6>-<$renewalNumber,2>"
 * @param baseNumber - the numeric base number
 * @param renewalNumber - the renewal counter (defaults to 0)
 * @returns the formatted policy number string, or empty string if template is empty
 */
export function formatPolicyNumber(template: string, baseNumber: number, renewalNumber: number = 0): string {
    if (!template) return '';
    return template.replace(VARIABLE_PATTERN, (_match, varName, widthStr) => {
        const width = parseInt(widthStr, 10);
        const value = varName === 'baseNumber' ? baseNumber : renewalNumber;
        return String(value).padStart(width, '0');
    });
}

/**
 * Returns a live preview example of what a policy number will look like using
 * the given template and starting policy number.
 * Returns empty string if template or startingPolicyNumber is not provided.
 */
export function getPolicyNumberPreview(
    template: string | undefined | null,
    startingPolicyNumber: number | undefined | null
): string {
    if (!template || startingPolicyNumber == null) return '';
    return formatPolicyNumber(template, startingPolicyNumber, 0);
}

/**
 * Validate a policy number format template.
 * Returns an error string if invalid, or null if valid.
 */
export function validatePolicyNumberFormat(template: string): string | null {
    if (!template || !template.trim()) return 'Template must not be empty';
    const regex = /<\$(\w+),(\d+)>/g;
    let match;
    let hasBaseNumber = false;
    let hasRenewalNumber = false;
    while ((match = regex.exec(template)) !== null) {
        const varName = match[1];
        if (varName === 'baseNumber') hasBaseNumber = true;
        else if (varName === 'renewalNumber') hasRenewalNumber = true;
        else return `Unknown variable: $${varName}`;
    }
    if (!hasBaseNumber) return 'Template must contain <$baseNumber,N>';
    if (!hasRenewalNumber) return 'Template must contain <$renewalNumber,N>';
    return null;
}
